import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import { useClockTick, useDashboard } from '@/hooks/useDashboard';
import { TopBar, type ViewName } from '@/components/TopBar';
// Banner is currently disabled — restore this import along with the <Banner />
// usage below. Left commented rather than deleted so the component stays wired.
// import { Banner } from '@/components/Banner';
import { Toasts } from '@/components/Toasts';
import { ServiceDrawer } from '@/components/ServiceDrawer';
import { OverviewView, type StatusFilter } from '@/views/OverviewView';
import { IncidentsView } from '@/views/IncidentsView';
import { HostsView } from '@/views/HostsView';
import { BloombergView } from '@/views/BloombergView';

export default function App() {
  const data = useDashboard();
  const [view, setView] = useState<ViewName>('overview');
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [focusMode, setFocusMode] = useState(false);
  const [openServiceKey, setOpenServiceKey] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Relative durations must keep ticking between fetches, or "down for 4m"
  // silently stays 4m for half an hour.
  useClockTick();

  // "/" focuses the filter, as in most developer tools.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);
      if (event.key === '/' && !typing) {
        event.preventDefault();
        setView('overview');
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const withRefresh = useCallback(
    async (action: () => Promise<unknown>) => {
      await action();
      await data.refresh();
    },
    [data]
  );

  const handleAck = useCallback(
    (id: number) => void withRefresh(() => api.ackIncident(id)),
    [withRefresh]
  );
  const handleResolve = useCallback(
    (id: number) => void withRefresh(() => api.resolveIncident(id)),
    [withRefresh]
  );
  const handleRunCheck = useCallback(
    (checkId: number) => withRefresh(() => api.runCheck(checkId)),
    [withRefresh]
  );

  const openService = data.services.find((s) => s.key === openServiceKey) ?? null;
  const openIncidentCount = data.openIncidents.filter((i) => !i.rootIncidentId).length;

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <TopBar
        view={view}
        onViewChange={setView}
        openIncidentCount={openIncidentCount}
        connection={data.connection}
        updatedAt={data.updatedAt}
        error={data.error}
        focusMode={focusMode}
        onFocusToggle={() => setFocusMode((f) => !f)}
        onRefresh={() => void data.refresh()}
      />

      {/* <Banner incidents={data.openIncidents} services={data.services} onAck={handleAck} /> */}

      <main id="main">
        {view === 'overview' && (
          <OverviewView
            services={data.services}
            openIncidents={data.openIncidents}
            agents={data.agents}
            history={data.history}
            loaded={data.loaded}
            filterText={filterText}
            onFilterTextChange={setFilterText}
            filterStatus={filterStatus}
            onFilterStatusChange={setFilterStatus}
            focusMode={focusMode}
            onOpenService={setOpenServiceKey}
            searchRef={searchRef}
          />
        )}

        {view === 'incidents' && (
          <IncidentsView
            incidents={data.incidents}
            services={data.services}
            onAck={handleAck}
            onResolve={handleResolve}
          />
        )}

        {view === 'hosts' && <HostsView agents={data.agents} />}

        {view === 'bloomberg' && <BloombergView />}
      </main>

      {openService && (
        <ServiceDrawer
          service={openService}
          incidents={data.incidents}
          onClose={() => setOpenServiceKey(null)}
          onRunCheck={handleRunCheck}
        />
      )}

      <Toasts transitions={data.transitions} onExpire={data.clearTransitions} />
    </>
  );
}
